/**
 * FinderlyFix admin (`/ops`): the operator console over EVERY partner
 * organisation, for FinderlyFix staff only (migration 076; the server's
 * `/partner/ops/*` answers 404 to anyone else, and the portal hides the
 * section unless `whoami.is_operator`).
 *
 *   /ops                  Pending approvals — approve or disable, each behind a dialog;
 *                         and Network fallback requests (082) — approve or decline with a reason;
 *                         and widget usage-limit requests (089) — grant (editable) or decline with a reason
 *   /ops/partners         All partners — search, lifecycle filter, load more
 *   /ops/partners/:id     One organisation — the admin detail and the overview facts, and its
 *                         Billing section (contractor billing, billing/OpsBillingPanel.tsx; `#billing`)
 *   /ops/billing          Billing accounts across organisations (screens/OpsBilling.tsx)
 *
 * Confirmation mirrors the bearer surface's `X-Admin-Confirm`: every write
 * names the partner id (sent from the dialog), and disabling also needs the
 * organisation's slug typed back, and so does "Release website" (frees the
 * host of a disabled or offboarded organisation for a new sign-up). The detail page's Network fallback switch
 * (any partner type) sends the partner id too, with a note (required to turn
 * it on).
 */
import { useEffect, useId, useState, type ReactNode } from 'react';
import { useSession } from '../auth/session';
import type {
  OpsFallbackRequest,
  OpsNetworkFallback,
  OpsPartner,
  OpsPartnerSite,
  OpsWidgetLimitRequest,
  PartnerLifecycle,
  WidgetLimitKind,
  WidgetLimitValues,
} from '../api/types';
import { Link } from '../router';
import { OpsBillingPanel } from '../billing/OpsBillingPanel';
import { planLabel } from '../org/status';
import { Empty, ErrorNotice, Loading, Modal, NotSet, PageHead, Segmented, Time, formatDate, formatDateTime, humanize, useToast } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';
import { PARTNER_TYPE_COPY } from './OrgSetup';

/** Fired after an approve/disable so the nav badge re-counts. */
export const OPS_CHANGED_EVENT = 'fx-ops-changed';
function notifyOpsChanged(): void {
  window.dispatchEvent(new Event(OPS_CHANGED_EVENT));
}

/** "3 pending" for the nav badge; null until known or for a non-operator. */
export function useOpsPendingCount(enabled: boolean): number | null {
  const { api } = useSession();
  const summary = useQuery(() => api.opsSummary(), ['ops-summary'], { enabled, pollMs: enabled ? 120_000 : undefined });
  const reload = summary.reload;
  useEffect(() => {
    if (!enabled) return;
    const on = () => void reload();
    window.addEventListener(OPS_CHANGED_EVENT, on);
    return () => window.removeEventListener(OPS_CHANGED_EVENT, on);
  }, [enabled, reload]);
  // Organisations waiting for approval plus Network fallback requests (082).
  // Plus widget usage-limit requests (089).
  return summary.data
    ? summary.data.counts.pending + (summary.data.fallback_requests ?? 0) + (summary.data.limit_requests ?? 0)
    : null;
}

const LIFECYCLE_LABEL: Record<PartnerLifecycle, string> = {
  pending: 'Pending',
  active: 'Active',
  disabled: 'Disabled',
  offboarding: 'Offboarding',
};
const LIFECYCLE_TONE: Record<PartnerLifecycle, string> = {
  pending: 'warn',
  active: 'ok',
  disabled: 'bad',
  offboarding: 'info',
};

export function LifecycleTag({ lifecycle }: { lifecycle: PartnerLifecycle }) {
  return <span className={`tag ${LIFECYCLE_TONE[lifecycle]}`}>{LIFECYCLE_LABEL[lifecycle]}</span>;
}

function typeLabel(type: string): string {
  return (PARTNER_TYPE_COPY as Record<string, { label: string } | undefined>)[type]?.label ?? humanize(type);
}

function host(origin: string): string {
  try {
    return new URL(origin).host;
  } catch {
    return origin.replace(/^https?:\/\//, '');
  }
}

function liveSites(p: OpsPartner): number {
  return p.sites.filter((s) => s.environment === 'live').length;
}

// ── Cells shared by the table, the cards and the detail page ─────────────
function SitesCell({ sites }: { sites: OpsPartnerSite[] }) {
  const live = sites.filter((s) => s.environment === 'live');
  if (!live.length) return <NotSet label="No sites" />;
  return (
    <ul className="ops-list">
      {live.map((s) => (
        <li key={s.site_key}>
          <span className="ops-strong">{s.label}</span>
          {s.origins.length ? (
            <span className="muted"> · {s.origins.slice(0, 2).map(host).join(', ')}{s.origins.length > 2 ? ` +${s.origins.length - 2}` : ''}</span>
          ) : (
            <span className="faint"> · any website</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function WidgetCell({ widget }: { widget: OpsPartner['widget'] }) {
  return widget.live ? (
    <span className="stack-xs">
      <span className="tag ok">Live</span>
      <span className="small muted">{widget.impressions_7d.toLocaleString('en-US')} loads · 7 days</span>
    </span>
  ) : (
    <span className="stack-xs">
      <span className="tag">Not live</span>
      {widget.last_impression_at ? (
        <span className="small muted">
          Last seen <Time iso={widget.last_impression_at} />
        </span>
      ) : null}
    </span>
  );
}

function TermsCell({ terms }: { terms: OpsPartner['terms'] }) {
  if (!terms) return <span className="tag warn">Not accepted</span>;
  return (
    <span className="stack-xs">
      <span>v{terms.terms_version}</span>
      <span className="small muted" title={formatDateTime(terms.accepted_at)}>
        {formatDate(terms.accepted_at)}
      </span>
    </span>
  );
}

function AgentCell({ agent }: { agent: OpsPartner['agent'] }) {
  if (!agent.active_tokens && !agent.last_used_at && !agent.pending_changes) return <span className="faint">None</span>;
  return (
    <span className="stack-xs">
      <span>
        {agent.active_tokens} {agent.active_tokens === 1 ? 'token' : 'tokens'}
        {agent.pending_changes ? <span className="tag warn" style={{ marginLeft: 6 }}>{agent.pending_changes} waiting</span> : null}
      </span>
      {agent.last_used_at ? (
        <span className="small muted">
          Used <Time iso={agent.last_used_at} />
        </span>
      ) : null}
    </span>
  );
}

function Owners({ emails }: { emails: string[] }) {
  if (!emails.length) return <NotSet label="No owner" />;
  return (
    <span className="ops-owner" title={emails.join(', ')}>
      {emails[0]}
      {emails.length > 1 ? <span className="muted"> +{emails.length - 1}</span> : null}
    </span>
  );
}

// ── Approve / disable / release website ───────────────────────────────────
type Pending = {
  kind: 'approve' | 'disable' | 'release';
  partner: Pick<OpsPartner, 'id' | 'display_name' | 'confirm_slug' | 'lifecycle' | 'site_release'>;
};

export function useLifecycleActions(onDone: () => void) {
  const [pending, setPending] = useState<Pending | null>(null);
  const dialog = pending ? <LifecycleDialog pending={pending} onClose={() => setPending(null)} onDone={onDone} /> : null;
  return { ask: setPending, dialog };
}

function LifecycleDialog({ pending, onClose, onDone }: { pending: Pending; onClose: () => void; onDone: () => void }) {
  const { api } = useSession();
  const toast = useToast();
  const [typed, setTyped] = useState('');
  const inputId = useId();
  const { partner, kind } = pending;
  const approve = kind === 'approve';
  const release = kind === 'release';
  const action = useMutation(() =>
    approve ? api.opsApprove(partner.id) : release ? api.opsReleaseSite(partner.id, typed.trim()) : api.opsDisable(partner.id, typed.trim()),
  );
  const slugOk = typed.trim().toLowerCase() === partner.confirm_slug;
  const siteHost = partner.site_release?.host ?? 'this website';
  const origins = partner.site_release?.verified_origins ?? [];

  const submit = async () => {
    const res = await action.run();
    if (!res) return;
    toast(approve ? `Approved ${partner.display_name}` : release ? `Released ${siteHost}` : `Disabled ${partner.display_name}`);
    notifyOpsChanged();
    onClose();
    onDone();
  };

  return (
    <Modal
      open
      title={
        approve ? `Approve ${partner.display_name}?` : release ? `Release ${siteHost} for new sign-ups?` : `Disable ${partner.display_name}?`
      }
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={action.pending}>
            Cancel
          </button>
          {approve ? (
            <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={action.pending}>
              {action.pending ? 'Approving…' : 'Approve organisation'}
            </button>
          ) : release ? (
            <button type="button" className="btn btn-danger" onClick={() => void submit()} disabled={action.pending || !slugOk}>
              {action.pending ? 'Releasing…' : 'Release website'}
            </button>
          ) : (
            <button type="button" className="btn btn-danger" onClick={() => void submit()} disabled={action.pending || !slugOk}>
              {action.pending ? 'Disabling…' : 'Disable organisation'}
            </button>
          )}
        </>
      }
    >
      <div className="stack-sm">
        {approve ? (
          <p style={{ margin: 0 }}>
            The organisation becomes active: its live widget can send real leads and the “Pending approval” badge goes away. This is
            recorded in its audit log under your name.
          </p>
        ) : (
          <>
            {release ? (
              <p style={{ margin: 0 }}>
                Frees <strong>{siteHost}</strong> so a new sign-up can prove it
                {origins.length ? (
                  <>
                    , and removes this organisation’s verified claim on {origins.map(host).join(', ')}
                  </>
                ) : null}
                . Nothing is deleted; its keys stay off. This is recorded in its audit log under your name.
              </p>
            ) : (
              <p style={{ margin: 0 }}>
                The organisation is suspended: its widget stops taking requests and its people lose access to live leads. Nothing is
                deleted, and you can approve it again later. This is recorded in its audit log under your name.
              </p>
            )}
            <div className="field">
              <label htmlFor={inputId}>
                Type <code className="ops-code">{partner.confirm_slug}</code> to confirm
              </label>
              <input
                id={inputId}
                className="input input-mono"
                value={typed}
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setTyped(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && slugOk) void submit();
                }}
                autoFocus
              />
            </div>
          </>
        )}
        <ErrorNotice error={action.error} />
      </div>
    </Modal>
  );
}

function ActionButtons({ partner, ask }: { partner: OpsPartner; ask: (p: Pending) => void }) {
  const canApprove = partner.lifecycle === 'pending' || partner.lifecycle === 'disabled';
  const canDisable = partner.lifecycle === 'pending' || partner.lifecycle === 'active';
  const canRelease = Boolean(partner.site_release?.eligible && !partner.site_release.released_at);
  return (
    <span className="row ops-actions" style={{ gap: 6 }}>
      {canApprove ? (
        <button type="button" className="btn btn-primary btn-sm" onClick={() => ask({ kind: 'approve', partner })}>
          Approve<span className="visually-hidden"> {partner.display_name}</span>
        </button>
      ) : null}
      {canDisable ? (
        <button type="button" className="btn btn-danger-subtle btn-sm" onClick={() => ask({ kind: 'disable', partner })}>
          Disable<span className="visually-hidden"> {partner.display_name}</span>
        </button>
      ) : null}
      {canRelease ? (
        <button type="button" className="btn btn-danger-subtle btn-sm" onClick={() => ask({ kind: 'release', partner })}>
          Release website<span className="visually-hidden"> of {partner.display_name}</span>…
        </button>
      ) : null}
    </span>
  );
}

function OpsEyebrow() {
  return <span>FinderlyFix admin</span>;
}

// ── /ops — Pending approvals ──────────────────────────────────────────────
export function OpsPending() {
  const { api } = useSession();
  const list = useQuery(() => api.opsPartners({ lifecycle: 'pending', limit: 200 }), ['ops-pending']);
  const { ask, dialog } = useLifecycleActions(() => void list.reload());
  const items = list.data?.items ?? [];

  return (
    <div>
      <PageHead
        eyebrow={<OpsEyebrow />}
        title="Pending approvals"
        lede="New organisations wait here until a FinderlyFix operator approves them. Check who they are and whether their widget is live before approving."
        actions={
          <Link className="btn btn-sm" to="/ops/partners">
            All partners
          </Link>
        }
      />
      <section className="panel" aria-label="Organisations waiting for approval">
        {list.loading && !list.data ? (
          <Loading label="Loading pending organisations" />
        ) : list.error ? (
          <div className="panel-body">
            <ErrorNotice error={list.error} title="Could not load pending organisations" />
            <button type="button" className="btn btn-sm" style={{ marginTop: 12 }} onClick={() => void list.reload()}>
              Try again
            </button>
          </div>
        ) : !items.length ? (
          <Empty level={2} title="Nothing waiting">No organisation is pending approval. New sign-ups appear here.</Empty>
        ) : (
          <>
            <div className="table-wrap collapse">
              <table className="data ops-table">
                <thead>
                  <tr>
                    <th scope="col">Organisation</th>
                    <th scope="col">Owner</th>
                    <th scope="col">Sites &amp; websites</th>
                    <th scope="col">Widget</th>
                    <th scope="col">Terms</th>
                    <th scope="col">AI agent</th>
                    <th scope="col">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <Link className="rowlink" to={`/ops/partners/${p.id}`}>
                          {p.display_name}
                        </Link>
                        <div className="small muted">
                          {typeLabel(p.partner_type)} · created {formatDate(p.created_at)}
                        </div>
                        <AgentSignupTags partner={p} />
                      </td>
                      <td className="ops-owner-col">
                        <Owners emails={p.owner_emails} />
                      </td>
                      <td>
                        <SitesCell sites={p.sites} />
                      </td>
                      <td>
                        <WidgetCell widget={p.widget} />
                      </td>
                      <td>
                        <TermsCell terms={p.terms} />
                      </td>
                      <td>
                        <AgentCell agent={p.agent} />
                      </td>
                      <td className="num">
                        <ActionButtons partner={p} ask={ask} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="cards" aria-label="Organisations waiting for approval">
              {items.map((p) => (
                <div key={p.id} className="card ops-card">
                  <div className="row-between" style={{ alignItems: 'flex-start' }}>
                    <Link className="ops-strong" to={`/ops/partners/${p.id}`}>
                      {p.display_name}
                    </Link>
                    <WidgetBadge widget={p.widget} />
                  </div>
                  <div className="small muted">
                    {typeLabel(p.partner_type)} · created {formatDate(p.created_at)}
                  </div>
                  <AgentSignupTags partner={p} />
                  <CardFact label="Owner">
                    <Owners emails={p.owner_emails} />
                  </CardFact>
                  <CardFact label="Sites">
                    <SitesCell sites={p.sites} />
                  </CardFact>
                  <CardFact label="Terms">
                    {p.terms ? `v${p.terms.terms_version} · ${formatDate(p.terms.accepted_at)}` : <span className="tag warn">Not accepted</span>}
                  </CardFact>
                  <CardFact label="AI agent">
                    <AgentCell agent={p.agent} />
                  </CardFact>
                  <div className="card-actions">
                    <ActionButtons partner={p} ask={ask} />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
      <FallbackRequests />
      <LimitRequests />
      {dialog}
    </div>
  );
}

/** Operator-readable reason the organisation's widget keys are off (migration 080). */
export function keysOffReason(reason: string | null | undefined): string {
  if (reason === 'unclaimed_expired') return 'Unclaimed for 14 days (AI agent sign-up)';
  if (reason === 'contact_reported') return 'Named contact reported “this wasn’t us” — review';
  return reason ? humanize(reason) : 'Switched off';
}

/**
 * "Created by AI agent", and its claim state, for an organisation an agent
 * signed up (migration 080). Nothing for one a person created.
 */
export function AgentSignupTags({ partner }: { partner: OpsPartner }) {
  const s = partner.agent_signup;
  if (!s && !partner.keys_disabled) return null;
  return (
    <span className="row" style={{ gap: 4, flexWrap: 'wrap' }} data-testid="ops-agent-signup">
      {s ? <span className="tag accent">Created by AI agent</span> : null}
      {s ? s.claimed ? <span className="tag ok">Claimed</span> : <span className="tag warn">Unclaimed</span> : null}
      {s?.reported_at ? <span className="tag bad">Reported</span> : null}
      {partner.keys_disabled ? <span className="tag bad">Keys off</span> : null}
    </span>
  );
}

function AgentSignupPanel({ partner }: { partner: OpsPartner }) {
  const s = partner.agent_signup;
  if (!s) return null;
  const client = [s.agent_client.name, s.agent_client.version].filter(Boolean).join(' ');
  return (
    <section className="panel" style={{ marginTop: 'var(--s5)' }} aria-labelledby="ops-agent-signup-h">
      <div className="panel-head">
        <h2 id="ops-agent-signup-h">AI agent sign-up</h2>
        <AgentSignupTags partner={partner} />
      </div>
      <div className="panel-body">
        <table className="kv">
          <tbody>
            <KV k="Website">{s.origin ?? s.host}</KV>
            <KV k="Proof">{s.proof_method === 'dns' ? 'DNS TXT record' : s.proof_method === 'file' ? 'Verification file' : <NotSet />}</KV>
            <KV k="Agent client">{client || <NotSet label="Not given" />}</KV>
            <KV k="Named contact">{s.contact_email ?? <NotSet />}</KV>
            <KV k="Claim">{s.claimed ? `Claimed ${formatDateTime(s.claimed_at)}` : 'Unclaimed'}</KV>
            {s.claimed ? null : <KV k="Keys stop (14 days)">{formatDateTime(s.disable_at)}</KV>}
            <KV k="Reported">{s.reported_at ? formatDateTime(s.reported_at) : <NotSet label="No" />}</KV>
            <KV k="Widget keys">
              {partner.keys_disabled
                ? `Off since ${formatDateTime(partner.keys_disabled.at)} — ${keysOffReason(partner.keys_disabled.reason)}`
                : 'Working'}
            </KV>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function WidgetBadge({ widget }: { widget: OpsPartner['widget'] }) {
  return widget.live ? <span className="tag ok">Live</span> : <span className="tag">Not live</span>;
}

function CardFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="ops-fact">
      <span className="card-label">{label}</span>
      <span>{children}</span>
    </div>
  );
}

// ── /ops/partners — All partners ──────────────────────────────────────────
type Filter = PartnerLifecycle | 'all';
const FILTERS: Filter[] = ['all', 'pending', 'active', 'disabled', 'offboarding'];

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function OpsPartners() {
  const { api } = useSession();
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim(), 250);
  const summary = useQuery(() => api.opsSummary(), ['ops-summary-page']);
  const [extra, setExtra] = useState<{ items: OpsPartner[]; cursor: string | null } | null>(null);
  const list = useQuery(
    () => api.opsPartners({ lifecycle: filter === 'all' ? undefined : filter, q: q || undefined }),
    ['ops-partners', filter, q],
  );
  useEffect(() => setExtra(null), [filter, q]);
  const more = useMutation((cursor: string) => api.opsPartners({ lifecycle: filter === 'all' ? undefined : filter, q: q || undefined }, cursor));
  const reloadAll = () => {
    setExtra(null);
    void list.reload();
    void summary.reload();
  };
  const { ask, dialog } = useLifecycleActions(reloadAll);

  const items = [...(list.data?.items ?? []), ...(extra?.items ?? [])];
  const cursor = extra ? extra.cursor : list.data?.next_cursor ?? null;
  const counts = summary.data?.counts;
  const searchId = useId();

  return (
    <div>
      <PageHead
        eyebrow={<OpsEyebrow />}
        title="All partners"
        lede="Every partner organisation on FinderlyFix, newest first."
        actions={
          <Link className="btn btn-sm" to="/ops/billing">
            Billing accounts
          </Link>
        }
      />
      <section className="panel">
        <div className="panel-head ops-toolbar">
          <label htmlFor={searchId} className="grow ops-search">
            <span className="visually-hidden">Search partners</span>
            <input
              id={searchId}
              type="search"
              className="input"
              placeholder="Search name, owner email, website or ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <Segmented
            label="Status"
            value={filter}
            onChange={setFilter}
            options={FILTERS.map((f) => ({
              value: f,
              label: `${f === 'all' ? 'All' : LIFECYCLE_LABEL[f]}${counts ? ` ${counts[f]}` : ''}`,
            }))}
          />
        </div>
        {list.loading && !list.data ? (
          <Loading label="Loading partners" />
        ) : list.error ? (
          <div className="panel-body">
            <ErrorNotice error={list.error} title="Could not load partners" />
            <button type="button" className="btn btn-sm" style={{ marginTop: 12 }} onClick={() => void list.reload()}>
              Try again
            </button>
          </div>
        ) : !items.length ? (
          <Empty title={q || filter !== 'all' ? 'No matching partners' : 'No partners yet'}>
            {q || filter !== 'all' ? 'Try another search or status.' : 'Organisations appear here when someone signs up.'}
          </Empty>
        ) : (
          <>
            <div className="table-wrap collapse">
              <table className="data ops-table">
                <thead>
                  <tr>
                    <th scope="col">Organisation</th>
                    <th scope="col">Status</th>
                    <th scope="col">Plan</th>
                    <th scope="col">Sites &amp; websites</th>
                    <th scope="col">Widget</th>
                    <th scope="col" className="num">Leads · 7d</th>
                    <th scope="col">Last activity</th>
                    <th scope="col">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <Link className="rowlink" to={`/ops/partners/${p.id}`}>
                          {p.display_name}
                        </Link>
                        <div className="small muted">
                          <Owners emails={p.owner_emails} />
                        </div>
                        <AgentSignupTags partner={p} />
                      </td>
                      <td>
                        <LifecycleTag lifecycle={p.lifecycle} />
                      </td>
                      <td>{planLabel(p.plan)}</td>
                      <td>
                        <SitesCell sites={p.sites} />
                      </td>
                      <td>
                        <WidgetBadge widget={p.widget} />
                      </td>
                      <td className="num">{p.leads_7d}</td>
                      <td>
                        <Time iso={p.last_activity_at} />
                      </td>
                      <td className="num">
                        {p.lifecycle === 'pending' ? <ActionButtons partner={p} ask={ask} /> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="cards">
              {items.map((p) => (
                <Link key={p.id} to={`/ops/partners/${p.id}`}>
                  <span className="row-between" style={{ flexWrap: 'nowrap' }}>
                    <span className="ops-strong">{p.display_name}</span>
                    <LifecycleTag lifecycle={p.lifecycle} />
                  </span>
                  <span className="small muted">
                    {planLabel(p.plan)} · {liveSites(p)} {liveSites(p) === 1 ? 'site' : 'sites'} · {p.widget.live ? 'widget live' : 'widget not live'}
                  </span>
                  <span className="small muted">
                    Last activity <Time iso={p.last_activity_at} />
                  </span>
                </Link>
              ))}
            </div>
            {cursor ? (
              <div className="panel-body" style={{ borderTop: '1px solid var(--line)' }}>
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={more.pending}
                  onClick={async () => {
                    const page = await more.run(cursor);
                    if (page) setExtra({ items: [...(extra?.items ?? []), ...page.items], cursor: page.next_cursor });
                  }}
                >
                  {more.pending ? 'Loading…' : 'Load more'}
                </button>
                <ErrorNotice error={more.error} />
              </div>
            ) : null}
          </>
        )}
      </section>
      {dialog}
    </div>
  );
}

// ── /ops/partners/:id — one organisation ──────────────────────────────────
export function OpsPartnerDetail({ id }: { id: string }) {
  const { api } = useSession();
  const detail = useQuery(() => api.opsPartner(id), ['ops-partner', id]);
  const { ask, dialog } = useLifecycleActions(() => void detail.reload());

  if (detail.loading && !detail.data) return <Loading label="Loading organisation" />;
  if (detail.error || !detail.data) {
    return (
      <div>
        <PageHead eyebrow={<OpsEyebrow />} title="Organisation" />
        {detail.error?.status === 404 ? (
          <Empty level={2} title="Organisation not found">
            <p style={{ margin: '0 0 12px' }}>There is no partner organisation with this ID.</p>
            <Link className="btn" to="/ops/partners">
              All partners
            </Link>
          </Empty>
        ) : (
          <ErrorNotice error={detail.error} title="Could not load this organisation" />
        )}
      </div>
    );
  }
  const d = detail.data;
  const o = d.overview;
  const flags = Object.entries(d.plan_flags ?? {});

  return (
    <div>
      <PageHead
        eyebrow={
          <Link to="/ops/partners" className="muted">
            FinderlyFix admin · All partners
          </Link>
        }
        title={
          <span className="row" style={{ gap: 10 }}>
            {d.display_name} <LifecycleTag lifecycle={o.lifecycle} />
          </span>
        }
        lede={`${typeLabel(d.partner_type)} · ${planLabel(d.plan)} plan · created ${formatDate(d.created_at)}`}
        actions={<ActionButtons partner={o} ask={ask} />}
      />
      <div className="ops-grid">
        <section className="panel">
          <div className="panel-head">
            <h2>Organisation</h2>
          </div>
          <div className="panel-body">
            <table className="kv">
              <tbody>
                <KV k="ID">
                  <code className="ops-code">{d.id}</code>
                </KV>
                <KV k="Owners">{o.owner_emails.length ? o.owner_emails.join(', ') : <NotSet label="No owner" />}</KV>
                <KV k="Members">{d.counts.members}</KV>
                <KV k="Terms">
                  {o.terms ? (
                    <>
                      v{o.terms.terms_version} · DPA {o.terms.dpa_version} · {formatDateTime(o.terms.accepted_at)}
                      {o.terms.accepted_by ? <span className="muted"> by {o.terms.accepted_by}</span> : null}
                    </>
                  ) : (
                    <span className="tag warn">Not accepted</span>
                  )}
                </KV>
                <KV k="Plan flags">
                  {flags.length ? flags.map(([k, v]) => `${k}: ${String(v)}`).join(' · ') : <NotSet label="None" />}
                </KV>
                <KV k="Media retention">{d.media_retention_days} days</KV>
                <KV k="Last activity">
                  <Time iso={o.last_activity_at} />
                </KV>
                <KV k="Updated">{formatDateTime(d.updated_at)}</KV>
                {o.offboarded_at ? <KV k="Offboarded">{formatDateTime(o.offboarded_at)}</KV> : null}
                {o.site_release?.released_at ? (
                  <KV k="Website">
                    Website released {formatDate(o.site_release.released_at)}
                    {o.site_release.released_by ? ` by ${o.site_release.released_by}` : ''}
                  </KV>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
        <section className="panel">
          <div className="panel-head">
            <h2>Activity</h2>
          </div>
          <div className="panel-body">
            <div className="ops-stats">
              <Stat label="Leads · 7 days" value={d.counts.leads_7d} />
              <Stat label="Leads · all time" value={d.counts.leads} />
              <Stat label="Widget loads · 7 days" value={o.widget.impressions_7d} />
              <Stat label="Contractors" value={d.counts.vendors} />
              <Stat label="Destinations" value={d.counts.destinations} />
              <Stat label="Agent tokens" value={o.agent.active_tokens} />
            </div>
            <p className="small muted" style={{ margin: '12px 0 0' }}>
              Widget {o.widget.live ? 'is live' : 'is not live'}
              {o.widget.last_impression_at ? (
                <>
                  {' '}
                  · last load <Time iso={o.widget.last_impression_at} />
                </>
              ) : null}
              {o.agent.last_used_at ? (
                <>
                  {' '}
                  · AI agent last used <Time iso={o.agent.last_used_at} />
                </>
              ) : null}
              {o.agent.pending_changes ? ` · ${o.agent.pending_changes} agent change(s) waiting` : null}
            </p>
          </div>
        </section>
      </div>
      <AgentSignupPanel partner={o} />
      <section className="panel" style={{ marginTop: 'var(--s5)' }}>
        <div className="panel-head">
          <h2>Sites &amp; websites</h2>
          <span className="small muted">{d.counts.sites} keys</span>
        </div>
        {o.sites.length ? (
          <>
          <div className="table-wrap collapse">
            <table className="data">
              <thead>
                <tr>
                  <th scope="col">Site</th>
                  <th scope="col">Key</th>
                  <th scope="col">Websites</th>
                  <th scope="col">Last load</th>
                </tr>
              </thead>
              <tbody>
                {o.sites.map((s) => (
                  <tr key={s.site_key} className={s.enabled ? undefined : 'dim'}>
                    <td>
                      {s.label} {s.environment === 'test' ? <span className="tag test">Test</span> : null}
                      {s.enabled ? null : <span className="tag">Off</span>}
                    </td>
                    <td>
                      <code className="ops-code wrap-anywhere">{s.site_key}</code>
                    </td>
                    <td>{s.origins.length ? s.origins.map(host).join(', ') : <span className="faint">Any website</span>}</td>
                    <td>
                      <Time iso={s.last_impression_at} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cards">
            {o.sites.map((s) => (
              <div key={s.site_key} className="card ops-card">
                <span className="ops-strong">
                  {s.label} {s.environment === 'test' ? <span className="tag test">Test</span> : null}
                  {s.enabled ? null : <span className="tag">Off</span>}
                </span>
                <code className="ops-code wrap-anywhere">{s.site_key}</code>
                <span className="small muted">
                  {s.origins.length ? s.origins.map(host).join(', ') : 'Any website'} · last load <Time iso={s.last_impression_at} />
                </span>
              </div>
            ))}
          </div>
          </>
        ) : (
          <Empty title="No sites yet" />
        )}
      </section>
      {d.network_fallback ? (
        <OpsFallbackPanel
          partnerId={d.id}
          partnerType={d.partner_type}
          data={d.network_fallback}
          onChanged={() => void detail.reload()}
        />
      ) : null}
      <OpsBillingPanel partnerId={d.id} displayName={d.display_name} />
      {dialog}
    </div>
  );
}

function KV({ k, children }: { k: string; children: ReactNode }) {
  return (
    <tr>
      <th scope="row">{k}</th>
      <td>{children}</td>
    </tr>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="ops-stat">
      <span className="ops-stat-value">{value.toLocaleString('en-US')}</span>
      <span className="card-label">{label}</span>
    </div>
  );
}

// ── Network fallback (migration 082) ──────────────────────────────────────
const ACCESS_WORDS: Record<OpsNetworkFallback['access'], string> = {
  self_serve: 'the partner switches it on and off itself',
  review: 'you check there is no HomeServe or warranty competition before it goes on (they ask; or switch it here)',
  unavailable: 'needs a neutral network, which is not connected: nothing would be sent',
};

function networkWords(network: OpsNetworkFallback['network']): string {
  return network === 'elocal' ? 'eLocal (majority-owned by HomeServe)' : 'a neutral network (not connected)';
}

/** The queue on /ops: open requests across every organisation. */
function FallbackRequests() {
  const { api } = useSession();
  const list = useQuery(() => api.opsFallbackRequests(), ['ops-fallback-requests']);
  const [deciding, setDeciding] = useState<{ kind: 'approve' | 'decline'; request: OpsFallbackRequest } | null>(null);
  const items = list.data?.items ?? [];
  return (
    <section className="panel" style={{ marginTop: 'var(--s5)' }} aria-labelledby="ops-fallback-h">
      <div className="panel-head">
        <h2 id="ops-fallback-h">Network fallback requests</h2>
        <span className="small muted">“Something else” organisations ask before it goes on</span>
      </div>
      {list.loading && !list.data ? (
        <Loading label="Loading Network fallback requests" />
      ) : list.error ? (
        <div className="panel-body">
          <ErrorNotice error={list.error} title="Could not load Network fallback requests" />
        </div>
      ) : !items.length ? (
        <Empty title="No requests">When a partner asks for the Network fallback, the request appears here.</Empty>
      ) : (
        <ul className="ops-fallback-list">
          {items.map((r) => (
            <li key={r.id} className="ops-fallback-item">
              <div className="stack-sm" style={{ gap: 4, minWidth: 0 }}>
                <span>
                  <Link className="ops-strong" to={`/ops/partners/${r.partner_id}`}>
                    {r.partner_display_name}
                  </Link>{' '}
                  <span className="small muted">· {typeLabel(r.partner_type)}</span>
                </span>
                {r.kind === 'enable' ? (
                  <span className="small">
                    Turn on for <strong>{r.site_label ?? r.site_key}</strong> · {networkWords(r.network)}
                  </span>
                ) : (
                  <span className="small">Asked to be told when a neutral network is available</span>
                )}
                <span className="small muted">
                  {r.requested_by_email ?? 'Unknown'}
                  {r.requested_by_agent ? ' (through an AI agent)' : ''} · <Time iso={r.created_at} />
                </span>
                {r.note ? <span className="small">Their note: “{r.note}”</span> : null}
              </div>
              <span className="row" style={{ gap: 6, flexShrink: 0 }}>
                {r.kind === 'enable' && r.access !== 'unavailable' ? (
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => setDeciding({ kind: 'approve', request: r })}>
                    Approve<span className="visually-hidden"> Network fallback for {r.partner_display_name}</span>
                  </button>
                ) : null}
                <button type="button" className="btn btn-danger-subtle btn-sm" onClick={() => setDeciding({ kind: 'decline', request: r })}>
                  {r.kind === 'enable' ? 'Decline' : 'Close'}
                  <span className="visually-hidden"> Network fallback request from {r.partner_display_name}</span>
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {deciding ? (
        <FallbackDecisionDialog
          kind={deciding.kind}
          request={deciding.request}
          onClose={() => setDeciding(null)}
          onDone={() => {
            notifyOpsChanged();
            void list.reload();
          }}
        />
      ) : null}
    </section>
  );
}

function FallbackDecisionDialog({
  kind,
  request: r,
  onClose,
  onDone,
}: {
  kind: 'approve' | 'decline';
  request: OpsFallbackRequest;
  onClose: () => void;
  onDone: () => void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const [text, setText] = useState('');
  const inputId = useId();
  const approve = kind === 'approve';
  const action = useMutation(() => (approve ? api.opsApproveFallbackRequest(r.id, text) : api.opsDeclineFallbackRequest(r.id, text)));
  const ok = approve || text.trim().length > 0;
  const submit = async () => {
    if (!(await action.run())) return;
    toast(approve ? `Network fallback turned on for ${r.partner_display_name}` : 'Request declined');
    onClose();
    onDone();
  };
  return (
    <Modal
      open
      title={approve ? `Turn on the Network fallback for ${r.partner_display_name}?` : r.kind === 'enable' ? 'Decline this request?' : 'Close this request?'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={action.pending}>
            Cancel
          </button>
          <button
            type="button"
            className={approve ? 'btn btn-primary' : 'btn btn-danger'}
            onClick={() => void submit()}
            disabled={action.pending || !ok}
          >
            {action.pending ? 'Saving…' : approve ? 'Approve and turn on' : r.kind === 'enable' ? 'Decline' : 'Close request'}
          </button>
        </>
      }
    >
      <div className="stack-sm">
        <p style={{ margin: 0 }}>
          {approve
            ? `The fallback goes on for ${r.site_label ?? r.site_key} (live and test keys): requests nobody on their team takes go to ${networkWords(r.network)}, for homeowners who tick its consent box. Confirm there is no HomeServe or warranty competition first. Recorded in their audit log under your name.`
            : 'The partner sees your reason on their Settings page and can ask again. Recorded in their audit log under your name.'}
        </p>
        <div className="field">
          <label htmlFor={inputId}>{approve ? 'Note for the audit log (optional)' : 'Reason (shown to the partner)'}</label>
          <textarea id={inputId} className="input" rows={3} maxLength={500} value={text} onChange={(e) => setText(e.target.value)} autoFocus />
        </div>
        <ErrorNotice error={action.error} />
      </div>
    </Modal>
  );
}

/** The partner detail page: the switch per site, any partner type, with a note. */
function OpsFallbackPanel({
  partnerId,
  partnerType,
  data,
  onChanged,
}: {
  partnerId: string;
  partnerType: string;
  data: OpsNetworkFallback;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState<OpsNetworkFallback['sites'][number] | null>(null);
  return (
    <section className="panel" style={{ marginTop: 'var(--s5)' }} aria-labelledby="ops-partner-fallback-h">
      <div className="panel-head">
        <h2 id="ops-partner-fallback-h">Network fallback</h2>
        <span className="small muted">{networkWords(data.network)}</span>
      </div>
      <div className="panel-body stack-sm">
        <p className="small muted" style={{ margin: 0 }}>
          {typeLabel(partnerType)}: {ACCESS_WORDS[data.access]}.
        </p>
        {data.sites.length ? (
          <ul className="ops-fallback-list">
            {data.sites.map((site) => (
              <li key={site.site_key} className="ops-fallback-item">
                <span className="stack-sm" style={{ gap: 2, minWidth: 0 }}>
                  <span className="ops-strong">{site.label}</span>
                  <code className="ops-code wrap-anywhere">{site.site_key}</code>
                </span>
                <span className="row" style={{ gap: 8 }}>
                  <span className={`tag ${site.enabled ? 'accent' : ''}`}>{site.enabled ? 'On' : 'Off'}</span>
                  <button type="button" className="btn btn-sm" onClick={() => setEditing(site)}>
                    {site.enabled ? 'Turn off' : 'Turn on'}
                    <span className="visually-hidden"> Network fallback for {site.label}</span>
                  </button>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="small muted" style={{ margin: 0 }}>
            No sites yet.
          </p>
        )}
        {data.requests.length ? (
          <>
            <h3 className="h-sub" style={{ fontSize: 14, margin: '8px 0 0' }}>
              Requests
            </h3>
            <ul className="stack-sm small" style={{ margin: 0, paddingLeft: 18 }}>
              {data.requests.map((r) => (
                <li key={r.id}>
                  {r.kind === 'enable' ? `Turn on for ${r.site_label ?? r.site_key}` : 'Tell us about a neutral network'} ·{' '}
                  <strong>{humanize(r.status)}</strong> · <Time iso={r.created_at} />
                  {r.decision_reason ? <span className="muted"> · “{r.decision_reason}”</span> : null}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
      {editing ? (
        <OpsFallbackSwitchDialog
          partnerId={partnerId}
          site={editing}
          data={data}
          onClose={() => setEditing(null)}
          onDone={onChanged}
        />
      ) : null}
    </section>
  );
}

function OpsFallbackSwitchDialog({
  partnerId,
  site,
  data,
  onClose,
  onDone,
}: {
  partnerId: string;
  site: OpsNetworkFallback['sites'][number];
  data: OpsNetworkFallback;
  onClose: () => void;
  onDone: () => void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const [note, setNote] = useState('');
  const inputId = useId();
  const turningOn = !site.enabled;
  const action = useMutation(() => api.opsSetNetworkFallback(partnerId, site.site_key, turningOn, note));
  const ok = !turningOn || note.trim().length > 0;
  const submit = async () => {
    if (!(await action.run())) return;
    toast(`Network fallback ${turningOn ? 'on' : 'off'} for ${site.label}`);
    onClose();
    onDone();
  };
  return (
    <Modal
      open
      title={`Turn ${turningOn ? 'on' : 'off'} the Network fallback for ${site.label}?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={action.pending}>
            Cancel
          </button>
          <button type="button" className={turningOn ? 'btn btn-primary' : 'btn btn-danger'} onClick={() => void submit()} disabled={action.pending || !ok}>
            {action.pending ? 'Saving…' : turningOn ? 'Turn on' : 'Turn off'}
          </button>
        </>
      }
    >
      <div className="stack-sm">
        <p style={{ margin: 0 }}>
          {turningOn
            ? `Both keys of ${site.label} offer ${networkWords(data.network)} to homeowners nobody on the partner’s team can help, behind the widget’s own consent box.`
            : `Both keys of ${site.label} stop offering the network. Takes effect within a minute.`}
          {turningOn && data.network === 'neutral' ? ' No neutral network is connected, so nothing would be sent.' : ''} Recorded in the partner’s audit
          log under your name.
        </p>
        <div className="field">
          <label htmlFor={inputId}>{turningOn ? 'Note (required: why it is going on)' : 'Note (optional)'}</label>
          <textarea id={inputId} className="input" rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
        </div>
        <ErrorNotice error={action.error} />
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Widget usage-limit requests (migration 089)
// ---------------------------------------------------------------------------

const limitNf = new Intl.NumberFormat('en-US');

function LimitRequests() {
  const { api } = useSession();
  const list = useQuery(() => api.opsWidgetLimitRequests(), ['ops-limit-requests']);
  const [deciding, setDeciding] = useState<{ kind: 'approve' | 'decline'; request: OpsWidgetLimitRequest } | null>(null);
  const items = list.data?.items ?? [];
  return (
    <section className="panel" style={{ marginTop: 'var(--s5)' }} aria-labelledby="ops-limits-h">
      <div className="panel-head">
        <h2 id="ops-limits-h">Widget usage-limit requests</h2>
        <span className="small muted">Partners asking for higher per-site limits</span>
      </div>
      {list.loading && !list.data ? (
        <Loading label="Loading usage-limit requests" />
      ) : list.error ? (
        <div className="panel-body">
          <ErrorNotice error={list.error} title="Could not load usage-limit requests" />
        </div>
      ) : !items.length ? (
        <Empty title="No requests">When a partner asks for higher widget usage limits, the request appears here.</Empty>
      ) : (
        <ul className="ops-fallback-list">
          {items.map((r) => (
            <li key={r.id} className="ops-fallback-item">
              <div className="stack-sm" style={{ gap: 4, minWidth: 0 }}>
                <span>
                  <Link className="ops-strong" to={`/ops/partners/${r.partner_id}`}>
                    {r.partner_display_name}
                  </Link>{' '}
                  <span className="small muted">· {r.site_label ?? r.site_key}</span>
                </span>
                <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
                  {r.current
                    .filter((c) => typeof r.requested[c.kind] === 'number')
                    .map((c) => (
                      <li key={c.kind}>
                        {c.label}: {limitNf.format(c.limit)} → <strong>{limitNf.format(r.requested[c.kind] as number)}</strong>
                        {c.used !== null ? <span className="muted"> · {limitNf.format(c.used)} used this window</span> : null}
                      </li>
                    ))}
                </ul>
                <span className="small muted">
                  {r.requested_by_email ?? 'Unknown'}
                  {r.requested_by_agent ? ' (through an AI agent)' : ''} · <Time iso={r.created_at} />
                </span>
                {r.note ? <span className="small">Their note: “{r.note}”</span> : null}
              </div>
              <span className="row" style={{ gap: 6, flexShrink: 0 }}>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setDeciding({ kind: 'approve', request: r })}>
                  Approve<span className="visually-hidden"> usage limits for {r.partner_display_name}</span>
                </button>
                <button type="button" className="btn btn-danger-subtle btn-sm" onClick={() => setDeciding({ kind: 'decline', request: r })}>
                  Decline<span className="visually-hidden"> usage-limit request from {r.partner_display_name}</span>
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {deciding ? (
        <LimitDecisionDialog
          kind={deciding.kind}
          request={deciding.request}
          onClose={() => setDeciding(null)}
          onDone={() => {
            notifyOpsChanged();
            void list.reload();
          }}
        />
      ) : null}
    </section>
  );
}

function LimitDecisionDialog({
  kind,
  request: r,
  onClose,
  onDone,
}: {
  kind: 'approve' | 'decline';
  request: OpsWidgetLimitRequest;
  onClose: () => void;
  onDone: () => void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const approve = kind === 'approve';
  const asked = r.current.filter((c) => typeof r.requested[c.kind] === 'number');
  const [grant, setGrant] = useState<Partial<Record<WidgetLimitKind, string>>>(() =>
    Object.fromEntries(asked.map((c) => [c.kind, String(r.requested[c.kind])])),
  );
  const [text, setText] = useState('');
  const textId = useId();
  const baseId = useId();
  const values = (): WidgetLimitValues | null => {
    const out: WidgetLimitValues = {};
    for (const c of asked) {
      const n = Number((grant[c.kind] ?? '').trim());
      if (!Number.isInteger(n) || n < 1) return null;
      out[c.kind] = n;
    }
    return out;
  };
  const action = useMutation(() =>
    approve ? api.opsApproveWidgetLimitRequest(r.id, values() ?? {}, text) : api.opsDeclineWidgetLimitRequest(r.id, text),
  );
  const ok = approve ? values() !== null : text.trim().length > 0;
  const submit = async () => {
    if (!(await action.run())) return;
    toast(approve ? `Usage limits raised for ${r.partner_display_name}` : 'Request declined');
    onClose();
    onDone();
  };
  return (
    <Modal
      open
      title={approve ? `Raise usage limits for ${r.site_label ?? r.site_key}?` : 'Decline this request?'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={action.pending}>
            Cancel
          </button>
          <button
            type="button"
            className={approve ? 'btn btn-primary' : 'btn btn-danger'}
            onClick={() => void submit()}
            disabled={action.pending || !ok}
          >
            {action.pending ? 'Saving…' : approve ? 'Approve' : 'Decline'}
          </button>
        </>
      }
    >
      <div className="stack-sm">
        <p style={{ margin: 0 }}>
          {approve
            ? 'These replace the default limits for this site (live key). Prefilled with what they asked for; you can grant less or more. Applies within a minute and is recorded in their audit log under your name.'
            : 'The partner sees your reason on their dashboard and can ask again. Recorded in their audit log under your name.'}
        </p>
        {approve ? (
          <div className="limit-grid">
            {asked.map((c) => (
              <div className="field" key={c.kind}>
                <label htmlFor={`${baseId}-${c.kind}`}>{c.label}</label>
                <input
                  id={`${baseId}-${c.kind}`}
                  className="input"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  aria-describedby={`${baseId}-${c.kind}-hint`}
                  value={grant[c.kind] ?? ''}
                  onChange={(e) => setGrant((g) => ({ ...g, [c.kind]: e.target.value }))}
                />
                <p className="hint" id={`${baseId}-${c.kind}-hint`}>
                  Now {limitNf.format(c.limit)}
                  {c.overridden ? ' (already raised)' : ` (default ${limitNf.format(c.default_limit)})`}
                </p>
              </div>
            ))}
          </div>
        ) : null}
        <div className="field">
          <label htmlFor={textId}>{approve ? 'Note for the audit log (optional)' : 'Reason (shown to the partner)'}</label>
          <textarea id={textId} className="input" rows={3} maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
        </div>
        <ErrorNotice error={action.error} />
      </div>
    </Modal>
  );
}
